import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Upload, Trash2, Pencil, Download, ExternalLink, Save, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LedgerView, Table } from "@/pages/EkycDesk";
import { MemberList, UserSearchAdd, useEkycUsers } from "@/components/EkycMembers";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const inputCls = "px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white";
const btnCls = "text-xs font-mono uppercase px-3 py-1.5 rounded bg-[#385C4B] text-white flex items-center gap-1 disabled:opacity-50";

export function EkycAdmin() {
  const navigate = useNavigate();
  const [sub, setSub] = useState("groups");
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const loadStaff = useCallback(() => api.get("/ekyc/staff").then(({ data }) => setStaff(data.staff)), []);
  useEffect(() => {
    api.get("/ekyc/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(formatApiError(e)));
    loadStaff().catch(() => {});
  }, [loadStaff]);
  if (!settings) return <div className="p-6 text-sm text-[#6B6B61]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="ekyc-admin">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-2xl font-bold text-[#1C1C19] mr-3">EKYC</h2>
        {[["groups", "Groups & Upload"], ["settings", "Rates & Checklist"], ["staff", "Staff Hisab"], ["report", "Report"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`ekyc-admin-tab-${k}`}
            className={`text-xs font-mono uppercase px-3 py-1.5 rounded ${sub === k ? "bg-[#385C4B] text-white" : "bg-white border border-[#DCDCD3] text-[#385C4B]"}`}>{l}</button>
        ))}
        <button onClick={() => navigate("/ekyc-desk")} className="ml-auto text-xs font-mono uppercase px-3 py-1.5 rounded border border-[#385C4B] text-[#385C4B] flex items-center gap-1" data-testid="ekyc-open-desk">
          <ExternalLink className="w-3.5 h-3.5" />Open Desk
        </button>
      </div>
      {sub === "groups" && <GroupsSection settings={settings} />}
      {sub === "settings" && <SettingsSection settings={settings} setSettings={setSettings} />}
      {sub === "staff" && <StaffSection staff={staff} reload={loadStaff} />}
      {sub === "report" && <ReportSection />}
    </div>
  );
}

function GroupsSection({ settings }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/ekyc/groups").then(({ data }) => setGroups(data.groups)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (g) => {
    if (!window.confirm(`"${g.name}" aur iske saare EKYC records delete karein?`)) return;
    await api.delete(`/ekyc/groups/${g.id}`).then(() => { toast.success("Deleted"); load(); }).catch((e) => toast.error(formatApiError(e)));
  };
  return (
    <div className="space-y-3">
      <button onClick={() => setEditing({ name: "", company: "INDANE", id_label: "", rate: "", member_ids: [] })} className={btnCls} data-testid="ekyc-new-group">
        <Plus className="w-3.5 h-3.5" />New EKYC Group
      </button>
      {editing && <GroupForm key={editing.id || "new"} initial={editing} settings={settings} onDone={() => { setEditing(null); load(); }} />}
      <div className="bg-white border border-[#EDEDE5] rounded-lg divide-y divide-[#F0F0EA]">
        {groups.length === 0 && <div className="p-4 text-sm text-[#6B6B61]">Abhi koi EKYC group nahi.</div>}
        {groups.map((g) => (
          <div key={g.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`ekyc-group-row-${g.id}`}>
            <div className="min-w-[200px]">
              <div className="font-bold text-sm">{g.name}</div>
              <div className="text-[11px] font-mono text-[#6B6B61]">{g.company} · {g.id_label} · Rate {inr(g.effective_rate)}{g.rate == null ? " (company)" : ""} · Members {g.member_ids?.length || 0}</div>
            </div>
            <div className="text-xs font-mono">
              <span className="text-amber-700">P {g.counts?.pending || 0}</span> · <span className="text-emerald-700">D {g.counts?.done || 0}</span> · <span className="text-[#C8553D]">F {g.counts?.failed || 0}</span>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <button onClick={() => navigate(`/ekyc-desk?view=chat&group=${g.id}`)} className="text-xs font-mono uppercase px-3 py-1.5 rounded border border-[#2F6F8F] text-[#2F6F8F] flex items-center gap-1" data-testid={`ekyc-chat-${g.id}`}>
                <MessageSquare className="w-3.5 h-3.5" />Chat
              </button>
              <UploadButton group={g} onDone={load} />
              <button onClick={() => setEditing(g)} className="p-1.5 rounded hover:bg-[#F0F0EA]" data-testid={`ekyc-edit-${g.id}`}><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(g)} className="p-1.5 rounded hover:bg-red-50 text-[#C8553D]" data-testid={`ekyc-delete-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-[#6B6B61] font-mono">Upload format (CSV / Excel): ID (ya LPG ID / Rel ID / Consumer No) · Name · Mobile · Remark. Duplicate ID auto-skip.</div>
    </div>
  );
}

function GroupForm({ initial, settings, onDone }) {
  const [f, setF] = useState({ ...initial, member_ids: initial.member_ids || [], rate: initial.rate ?? "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const { users, byId } = useEkycUsers();
  const save = async () => {
    const body = { name: f.name, company: f.company, id_label: f.id_label, rate: f.rate === "" ? null : Number(f.rate), member_ids: f.member_ids };
    try {
      if (f.id) await api.put(`/ekyc/groups/${f.id}`, body);
      else await api.post("/ekyc/groups", body);
      toast.success("Saved");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="bg-white border border-[#385C4B]/40 rounded-lg p-3 space-y-3" data-testid="ekyc-group-form">
      <div className="flex flex-wrap gap-2">
        <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Group Name (e.g. RC INDANE EKYC)" className={`${inputCls} flex-1 min-w-[200px]`} data-testid="ekyc-form-name" />
        <select value={f.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="ekyc-form-company">
          {settings.companies.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={f.id_label} onChange={(e) => set("id_label", e.target.value)} placeholder={`ID label (${settings.default_id_label[f.company]})`} className={`${inputCls} w-40`} data-testid="ekyc-form-idlabel" />
        <input type="number" value={f.rate} onChange={(e) => set("rate", e.target.value)} placeholder={`Rate ₹ (company ${settings.rates[f.company]})`} className={`${inputCls} w-44`} data-testid="ekyc-form-rate" />
      </div>
      <div className="space-y-2" data-testid="ekyc-form-members">
        <div className="text-[10px] font-mono uppercase text-[#6B6B61]">Members ({f.member_ids.length}) · EKYC Staff / Agency / Team staff, koi bhi user</div>
        {users === null ? <div className="text-xs text-[#6B6B61]">Loading users…</div> : (
          <>
            <UserSearchAdd users={users} excludeIds={f.member_ids} onPick={(id) => set("member_ids", [...f.member_ids, id])} />
            <MemberList ids={f.member_ids} byId={byId} onRemove={(id) => set("member_ids", f.member_ids.filter((x) => x !== id))} />
          </>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={save} className={btnCls} data-testid="ekyc-form-save"><Save className="w-3.5 h-3.5" />Save</button>
        <button onClick={onDone} className="text-xs px-3 py-1.5" data-testid="ekyc-form-cancel">Cancel</button>
      </div>
    </div>
  );
}

function UploadButton({ group, onDone }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/ekyc/groups/${group.id}/upload`, fd);
      toast.success(`${data.inserted} naye add · ${data.duplicates_in_file + data.duplicates_existing} duplicate skip`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={`${btnCls} cursor-pointer`} data-testid={`ekyc-upload-${group.id}`}>
      <Upload className="w-3.5 h-3.5" />{busy ? "Uploading…" : "Upload"}
      <input type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} disabled={busy} />
    </label>
  );
}

function SettingsSection({ settings, setSettings }) {
  const [rates, setRates] = useState(settings.rates);
  const [lists, setLists] = useState(Object.fromEntries(settings.companies.map((c) => [c, (settings.checklists[c] || []).join("\n")])));
  const save = async () => {
    try {
      const body = { rates, checklists: Object.fromEntries(Object.entries(lists).map(([c, t]) => [c, t.split("\n")])) };
      const { data } = await api.put("/ekyc/settings", body);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Rates & checklist saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        {settings.companies.map((c) => (
          <div key={c} className="bg-white border border-[#EDEDE5] rounded-lg p-3 space-y-2">
            <div className="font-bold text-[#385C4B]">{c}</div>
            <label className="text-[10px] font-mono uppercase text-[#6B6B61] block">Rate per KYC Done (₹)</label>
            <input type="number" value={rates[c]} onChange={(e) => setRates((r) => ({ ...r, [c]: e.target.value }))} className={`${inputCls} w-full`} data-testid={`ekyc-rate-${c}`} />
            <label className="text-[10px] font-mono uppercase text-[#6B6B61] block">Checklist steps (ek line = ek step)</label>
            <textarea rows={4} value={lists[c]} onChange={(e) => setLists((l) => ({ ...l, [c]: e.target.value }))} placeholder={"OTP verify\nFace capture\nPortal submit"} className={`${inputCls} w-full font-mono text-xs`} data-testid={`ekyc-checklist-${c}`} />
          </div>
        ))}
      </div>
      <button onClick={save} className={btnCls} data-testid="ekyc-settings-save"><Save className="w-3.5 h-3.5" />Save</button>
      <div className="text-[11px] text-[#6B6B61]">Group me alag rate set ho to wahi lagega. Rate change hone se purane Done ka amount nahi badlega.</div>
    </div>
  );
}

function StaffSection({ staff, reload }) {
  const [sel, setSel] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [pay, setPay] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), mode: "UPI", note: "" });
  const open = useCallback((s) => {
    setSel(s);
    api.get(`/ekyc/ledger/${s.id}`).then(({ data }) => setLedger(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const addPay = async () => {
    try {
      await api.post("/ekyc/payments", { staff_id: sel.id, ...pay, amount: Number(pay.amount) });
      toast.success("Payment saved");
      setPay((p) => ({ ...p, amount: "", note: "" }));
      open(sel);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="space-y-3">
      <Table testid="ekyc-staff-table" head={["Staff", "Aaj Done", "Total Done", "Earned", "Paid", "Balance", ""]}
        rows={staff.map((s) => [s.display_name, s.today_done, s.total_done, inr(s.earned), inr(s.paid), inr(s.balance),
          <button key="v" onClick={() => open(s)} className="text-xs underline text-[#385C4B]" data-testid={`ekyc-staff-view-${s.id}`}>Hisab</button>])} />
      {sel && ledger && (
        <div className="space-y-3">
          <div className="font-bold">{sel.display_name}</div>
          <div className="bg-white border border-[#EDEDE5] rounded-lg p-3 flex flex-wrap gap-2 items-center" data-testid="ekyc-payment-form">
            <input type="number" placeholder="Amount ₹" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className={`${inputCls} w-28`} data-testid="ekyc-pay-amount" />
            <input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className={inputCls} data-testid="ekyc-pay-date" />
            <select value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })} className={inputCls} data-testid="ekyc-pay-mode">
              {["UPI", "Cash", "Bank"].map((m) => <option key={m}>{m}</option>)}
            </select>
            <input placeholder="Note" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} className={`${inputCls} flex-1 min-w-[120px]`} data-testid="ekyc-pay-note" />
            <button onClick={addPay} disabled={!pay.amount} className={btnCls} data-testid="ekyc-pay-save"><Plus className="w-3.5 h-3.5" />Add Payment</button>
          </div>
          <LedgerView data={ledger} />
        </div>
      )}
    </div>
  );
}

function ReportSection() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rep, setRep] = useState(null);
  const load = useCallback(() => {
    api.get("/ekyc/report", { params: { date_from: from, date_to: to } }).then(({ data }) => setRep(data)).catch((e) => toast.error(formatApiError(e)));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  const download = async () => {
    try {
      const res = await api.get("/ekyc/report.xlsx", { params: { date_from: from, date_to: to }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EKYC_REPORT_${from || "ALL"}_${to || "ALL"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="ekyc-report-from" />
        <span className="text-xs">to</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="ekyc-report-to" />
        <button onClick={download} className={btnCls} data-testid="ekyc-report-download"><Download className="w-3.5 h-3.5" />Excel</button>
      </div>
      {rep && (
        <>
          <Table testid="ekyc-report-groups" title="Group / Company wise" head={["Group", "Company", "Pending", "Done", "Failed"]}
            rows={rep.groups.map((g) => [g.group_name, g.company, g.pending || 0, g.done || 0, g.failed || 0])} />
          <Table testid="ekyc-report-rows" title="Staff / Date wise" head={["Date", "Staff", "Company", "Group", "Done", "Failed", "Amount"]}
            rows={rep.rows.map((r) => [r.date, r.staff, r.company, r.group_name, r.done, r.failed, inr(r.amount)])} />
        </>
      )}
    </div>
  );
}
